import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import { apiClient } from '../client/client';

/**
 * Request body for POST /api/open-ai/fps (FPS / game performance).
 * Backend may use "open-aı" (Turkish ı) in path; use same in base if required.
 */
export interface FpsRequestModel {
  properties: Record<string, string>;
  game: string;
}

/**
 * Request body for POST /api/open-ai/compatibility (software compatibility).
 */
export interface SoftwareCompatibilityRequestModel {
  properties: Record<string, string>;
  application: string;
}

/**
 * OpenAI-backed FPS and software compatibility endpoints.
 * Responses are StringServiceResult (plain string in data).
 * Path matches swagger: /api/open-aı/ (Turkish dotless ı).
 */
export class OpenAIService {
  private static readonly BASE = '/api/open-ai';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  /**
   * POST FPS – returns plain string result (e.g. FPS analysis) in data.
   * External service call may take several seconds.
   */
  async getFps(body: FpsRequestModel): Promise<ServiceResultT<string>> {
    const { data } = await this.client.post<ServiceResultT<string>>(
      `${OpenAIService.BASE}/fps`,
      body
    );
    return data;
  }

  /**
   * POST compatibility – returns plain string result (e.g. compatibility analysis) in data.
   * External service call may take several seconds.
   */
  async getCompatibility(
    body: SoftwareCompatibilityRequestModel
  ): Promise<ServiceResultT<string>> {
    const { data } = await this.client.post<ServiceResultT<string>>(
      `${OpenAIService.BASE}/compatibility`,
      body
    );
    return data;
  }
}

export const openAIService = new OpenAIService();
